import AkyoQuestion, {type NativeQuestion} from './AkyoQuestion';
import {formatChemistryProse} from './chemical-typography';
import { useEffect, useRef, useState } from "react";
import { Check, KeyRound, LockKeyhole, LogOut, RefreshCw, ShieldAlert, WifiOff } from "lucide-react";

type ReviewOption = { letter: string; text: string; selected: boolean; correct: boolean };
type ReviewQuestion = {
  number: number;
  id: string;
  prompt: string;
  explanation: string;
  imageUrl: string;
  status: "correct" | "incorrect" | "unanswered";
  options: ReviewOption[];
};
type StudentReview = {
  totalQuestions: number;
  answeredCount: number;
  correctCount: number;
  score20: number | null;
  questions: ReviewQuestion[];
};

type SecureState = {
  nativeReview?: {question:NativeQuestion;correction:any;status:string;answer:unknown}[];
  secure: true;
  session: {
    id: string;
    code: string;
    mode: "prerequisite" | "evaluated";
    theme: string;
    chapter: string;
    status: "lobby" | "question" | "locked" | "done" | "finished";
    currentIndex: number;
    totalQuestions: number;
  };
  student: { fullName: string; studentCode: string };
  question: { native?: NativeQuestion; id: string; prompt: string; imageUrl?: string; options: Array<{ id: string; text: string }> } | null;
  lockedAt?: string | null;
  lockReason?: string | null;
  remainingSeconds?: number | null;
  review?: StudentReview | null;
};

async function request<T>(path: string, options?: RequestInit) {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }, ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error ?? "Une erreur est survenue") as Error & { status?: number; loginRequired?: boolean; locked?: boolean };
    error.status = response.status;
    error.loginRequired = Boolean(body.loginRequired);
    error.locked = Boolean(body.locked);
    throw error;
  }
  return body as T;
}

function MathContent({ text, className = "" }: { text: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const render = () => (window as any).MathJax?.typesetPromise?.(ref.current ? [ref.current] : undefined).catch(() => {});
    if ((window as any).MathJax?.typesetPromise) { void render(); return; }
    if (!document.getElementById("competenceo-mathjax")) {
      (window as any).MathJax = { tex: { inlineMath: [["$", "$"], ["\\(", "\\)"]], displayMath: [["$$", "$$"], ["\\[", "\\]"]] }, svg: { fontCache: "global" } };
      const script = document.createElement("script");
      script.id = "competenceo-mathjax";
      script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js";
      script.async = true;
      script.onload = render;
      document.head.appendChild(script);
    }
  }, [text]);
  return <div ref={ref} className={className}>{formatChemistryProse(text)}</div>;
}

function formatTimer(seconds?: number | null) {
  if (seconds === null || seconds === undefined) return "";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function SecureQuizStudent({ code }: { code: string }) {
  const [state, setState] = useState<SecureState | null>(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const stateRef = useRef<SecureState | null>(null);
  stateRef.current = state;

  async function refresh(silent = false) {
    try {
      const next = await request<SecureState>(`/api/secure-qcm/join/${code}`);
      setState(next);
      setNeedLogin(false);
      if (!silent) setError("");
    } catch (reason) {
      const failure = reason as Error & { status?: number; loginRequired?: boolean };
      if (failure.status === 401 || failure.loginRequired) setNeedLogin(true);
      else if (!silent) setError(failure.message);
    }
  }

  function sendSecurityEvent(type: string, detail: Record<string, unknown> = {}, beacon = false) {
    const current = stateRef.current;
    if (!current || current.session.status === "finished" || current.session.status === "done") return;
    const path = `/api/secure-qcm/join/${code}/event`;
    const body = JSON.stringify({ type, detail });
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(path, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(path, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  }

  useEffect(() => { void refresh(); }, [code]);
  useEffect(() => {
    if (!state || state.session.status === "finished") return;
    const timer = window.setInterval(() => void refresh(true), 900);
    return () => window.clearInterval(timer);
  }, [state?.session.id, state?.session.status]);

  useEffect(() => {
    const visibility = () => {
      if (document.visibilityState === "hidden") {
        const current = stateRef.current;
        if (current?.session.status === "question") setState({ ...current, session: { ...current.session, status: "locked" } });
        sendSecurityEvent("hidden", { visibilityState: document.visibilityState }, true);
      } else {
        sendSecurityEvent("visible", { visibilityState: document.visibilityState });
        void refresh(true);
      }
    };
    const blur = () => { if (document.visibilityState === "visible") sendSecurityEvent("blur", { visibilityState: document.visibilityState }); };
    const pagehide = () => sendSecurityEvent("pagehide", {}, true);
    const goOffline = () => { setOffline(true); sendSecurityEvent("offline"); };
    const goOnline = () => { setOffline(false); sendSecurityEvent("online"); void refresh(true); };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", blur);
    window.addEventListener("pagehide", pagehide);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", blur);
      window.removeEventListener("pagehide", pagehide);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [code]);

  async function login(event: React.FormEvent) {
    event.preventDefault(); setSending(true); setError("");
    try {
      const result = await request<{ user: { role: string } }>("/api/auth/login", { method: "POST", body: JSON.stringify({ identifier, password }) });
      if (result.user.role !== "student") throw new Error("Utilise ton compte élève pour rejoindre le QCM");
      await refresh();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Connexion impossible"); }
    finally { setSending(false); }
  }

  async function answer(questionId: string, answerId: string) {
    if (!state || state.session.status !== "question" || sending || offline) return;
    setSending(true); setError("");
    try {
      await request(`/api/secure-qcm/join/${code}/answer`, { method: "POST", body: JSON.stringify({ questionId, answerId }) });
      await refresh(true);
    } catch (reason) {
      const failure = reason as Error & { locked?: boolean };
      if (failure.locked && state) setState({ ...state, session: { ...state.session, status: "locked" } });
      setError(failure.message);
      await refresh(true);
    } finally { setSending(false); }
  }

  async function logout() {
    sendSecurityEvent("pagehide", { action: "logout" }, true);
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setState(null); setNeedLogin(true); setIdentifier(""); setPassword(""); setError("");
  }

  if (needLogin && !state) return <main className="secure-qcm-student-shell"><section className="secure-qcm-login-card">
    <img src="/competenceo-logo-192.png" alt="CompetenceO" /><div className="secure-qcm-badge">QCM individuel sécurisé</div><KeyRound size={34} /><h1>Connecte-toi</h1><p>Utilise ton code élève et ton mot de passe CompetenceO.</p>
    <form onSubmit={login}><label><span>Code élève</span><input autoCapitalize="characters" autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} required /></label><label><span>Mot de passe</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>{error && <div className="secure-qcm-error">{error}</div>}<button disabled={sending}>{sending ? "Connexion…" : "Rejoindre"}</button></form>
  </section></main>;

  if (!state) return <main className="secure-qcm-student-shell"><section className="secure-qcm-state"><RefreshCw className="spin" size={34} /><h1>Connexion au QCM…</h1>{error && <p>{error}</p>}</section></main>;

  const { session, student, question, remainingSeconds } = state;
  const visibleLetters = ["A", "B", "C", "D"];

  return <main className="secure-qcm-student-shell">
    <header className="secure-qcm-student-head"><div><img src="/competenceo-logo-192.png" alt="" /><span>{student.fullName}</span><b>Mode sécurisé</b></div><button onClick={() => void logout()}><LogOut size={18} /></button></header>
    {offline && <div className="secure-qcm-network"><WifiOff size={18} /><span>Connexion perdue — tes réponses sont momentanément suspendues. Ce n’est pas considéré comme une sortie de l’activité.</span></div>}

    {session.status === "lobby" && <section className="secure-qcm-state"><ShieldAlert size={46} /><span>{session.theme ? `${session.theme} · ` : ""}{session.chapter}</span><h1>Tu es connecté</h1><p>Reste sur cette page. Le professeur va lancer le QCM.</p></section>}

    {session.status === "locked" && <section className="secure-qcm-lock"><LockKeyhole size={58} /><span>QCM suspendu</span><h1>Vous avez quitté l’activité</h1><p>Votre enseignant doit autoriser la reprise. Actualiser ou rouvrir la page ne retire pas ce verrou.</p>{state.lockedAt && <small>Sortie détectée à {new Date(state.lockedAt).toLocaleTimeString("fr-FR")}</small>}{error && <div className="secure-qcm-error">{error}</div>}</section>}

    {session.status === "question" && question && <section className="secure-qcm-question">
      <div className="secure-qcm-progress"><div><span>Question {session.currentIndex + 1} / {session.totalQuestions}</span><div><i style={{ width: `${Math.round((session.currentIndex / Math.max(1, session.totalQuestions)) * 100)}%` }} /></div></div>{remainingSeconds !== null && remainingSeconds !== undefined && <strong className={remainingSeconds <= 60 ? "urgent" : ""}>{formatTimer(remainingSeconds)}</strong>}</div>
      {!question.native && <MathContent text={question.prompt} className="secure-qcm-prompt" />}
      {question.imageUrl && <img className="secure-qcm-image" src={question.imageUrl} alt="Support de la question" />}
      {question.native && <AkyoQuestion key={question.id} question={question.native} disabled={sending||offline} onAnswer={async answer=>{await request('/api/secure-qcm/join/'+code+'/answer',{method:'POST',body:JSON.stringify({questionId:question.id,answer})});await refresh();}}/>}
      <div className="secure-qcm-options">{question.options.map((option, index) => <button key={option.id} disabled={sending || offline} onClick={() => void answer(question.id, option.id)}><b>{visibleLetters[index]}</b><MathContent text={option.text} />{sending && <Check size={18} />}</button>)}</div>
      <p className="secure-qcm-help">Une fois une réponse envoyée, tu passes à la question suivante.</p>{error && <div className="secure-qcm-error">{error}</div>}
    </section>}

    {session.status === "done" && <section className="secure-qcm-state"><Check size={52} /><h1>Réponses terminées</h1><p>Tu as répondu aux {session.totalQuestions} questions. Reste sur cette page jusqu’à la fin du QCM.</p></section>}
    {session.status === "finished" && <section className="secure-qcm-review" aria-label="Correction de mon QCM">
      <header className="secure-qcm-review-header">
        <Check size={42} aria-hidden="true" />
        <div><span>{session.theme ? `${session.theme} · ` : ""}{session.chapter}</span><h1>Correction de ton QCM</h1>
          <p>Retrouve tes réponses : <b>vert = juste</b>, <b>rouge = faux</b>. Lis la bonne réponse et l’explication pour progresser.</p></div>
      </header>
      {state.review ? <>
        <div className="secure-qcm-review-summary">
          <div><span>Bonnes réponses</span><strong>{state.review.correctCount} / {state.review.totalQuestions}</strong></div>
          <div><span>Questions traitées</span><strong>{state.review.answeredCount} / {state.review.totalQuestions}</strong></div>
          {session.mode === "evaluated" && state.review.score20 !== null &&
            <div><span>Note du QCM</span><strong>{state.review.score20.toFixed(1).replace(".0", "")} / 20</strong></div>}
        </div>
        {session.mode === "prerequisite" && <p className="secure-qcm-review-diagnostic">Ce QCM était un diagnostic : le résultat n’est pas une note.</p>}
        <div className="secure-qcm-review-questions">
          {state.nativeReview?.map(item=><article key={item.question.instanceId}><strong>{item.status}</strong><AkyoQuestion question={item.question} response={item.answer} correction={item.correction}/></article>)}
          {state.review.questions.map((item) => <article key={item.id} className={`secure-qcm-review-question ${item.status}`}>
            <div className="secure-qcm-review-question-heading">
              <strong>Question {item.number}</strong>
              <span>{item.status === "correct" ? "Bonne réponse" : item.status === "incorrect" ? "Réponse incorrecte" : "Sans réponse"}</span>
            </div>
            <MathContent text={item.prompt} className="secure-qcm-review-prompt" />
            {item.imageUrl && <img className="secure-qcm-review-image" src={item.imageUrl} alt="Document associé à la question" />}
            <div className="secure-qcm-review-options">{item.options.map((option) =>
              <div key={option.letter} className={`secure-qcm-review-option${option.correct ? " correct" : option.selected ? " incorrect" : ""}`}>
                <strong className="secure-qcm-review-letter">{option.letter}</strong>
                <MathContent text={option.text} className="secure-qcm-review-answer-text" />
                <span className="secure-qcm-review-option-label">
                  {option.correct && option.selected ? "Ta réponse · Juste" : option.correct ? "Bonne réponse" : option.selected ? "Ta réponse · Fausse" : ""}
                </span>
              </div>)}</div>
            {item.explanation && <div className="secure-qcm-review-explanation"><b>Explication</b><MathContent text={item.explanation} /></div>}
          </article>)}
        </div>
      </> : <p className="secure-qcm-review-no-participation">Aucune copie personnelle disponible pour ce QCM.</p>}
      <a className="secure-qcm-review-return" href="/">Retour à CompetenceO</a>
    </section>}
  </main>;
}
